import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { 
  ArrowLeft, 
  Download, 
  Trash2, 
  Calendar, 
  Clock, 
  User, 
  Mail, 
  FileText,
  Tag,
  Eye,
  Check,
  X,
  Images
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Label } from '../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../../components/ui/alert-dialog';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ImageDetailPage = () => {
  const { imageId } = useParams();
  const navigate = useNavigate();
  const [image, setImage] = useState(null);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Editable fields
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [note, setNote] = useState('');
  const [usageStatus, setUsageStatus] = useState('nicht_verwendet');

  useEffect(() => {
    fetchData();
  }, [imageId]);

  const fetchData = async () => {
    try {
      const [imageRes, categoriesRes] = await Promise.all([
        axios.get(`${API}/images/${imageId}`),
        axios.get(`${API}/categories`)
      ]);
      
      setImage(imageRes.data);
      setCategories(categoriesRes.data);
      setSelectedCategories(imageRes.data.categories || []);
      setNote(imageRes.data.note || '');
      setUsageStatus(imageRes.data.usage_status || 'nicht_verwendet');
    } catch (error) {
      console.error('Failed to fetch image:', error);
      toast.error('Bild konnte nicht geladen werden');
      navigate('/admin/media');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/images/${imageId}`, {
        categories: selectedCategories,
        note,
        usage_status: usageStatus
      });
      toast.success('Änderungen gespeichert');
      fetchData();
    } catch (error) {
      console.error('Failed to save:', error);
      toast.error('Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  const handleDownload = async () => {
    try {
      const response = await axios.get(`${API}/images/${imageId}/download`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = image.filename;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success('Download gestartet');
      fetchData(); // Refresh to update download count
    } catch (error) {
      console.error('Download failed:', error);
      toast.error('Download fehlgeschlagen');
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/images/${imageId}`);
      toast.success('Bild gelöscht');
      navigate('/admin/media');
    } catch (error) {
      console.error('Delete failed:', error);
      toast.error('Löschen fehlgeschlagen');
    }
  };

  const toggleCategory = (categoryId) => {
    if (selectedCategories.includes(categoryId)) {
      setSelectedCategories(prev => prev.filter(id => id !== categoryId));
    } else {
      setSelectedCategories(prev => [...prev, categoryId]);
    }
  };

  const getDownloadBadge = () => {
    if (!image) return null;
    if (image.download_count === 0) return { label: 'Neu', color: 'bg-green-100 text-green-800' };
    if (image.download_count >= 3) return { label: 'Mehrfach verwendet', color: 'bg-purple-100 text-purple-800' };
    return { label: 'Heruntergeladen', color: 'bg-blue-100 text-blue-800' };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!image) {
    return (
      <div className="text-center py-16">
        <Images className="w-16 h-16 mx-auto text-slate-300 mb-4" />
        <h3 className="text-lg font-medium text-slate-900 mb-2">Bild nicht gefunden</h3>
        <Link to="/admin/media">
          <Button>Zur Medienbibliothek</Button>
        </Link>
      </div>
    );
  }

  const badge = getDownloadBadge();

  return (
    <div className="space-y-6" data-testid="image-detail-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/admin/media">
            <Button variant="ghost" size="icon" data-testid="back-button">
              <ArrowLeft className="w-5 h-5" />
            </Button>
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 heading-app">Bilddetails</h1>
            <p className="text-slate-500">{image.filename}</p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <Button onClick={handleDownload} data-testid="download-button">
            <Download className="w-4 h-4 mr-2" />
            Herunterladen
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline" className="text-red-600 hover:text-red-700" data-testid="delete-button">
                <Trash2 className="w-4 h-4" />
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Bild löschen?</AlertDialogTitle>
                <AlertDialogDescription>
                  Diese Aktion kann nicht rückgängig gemacht werden. Das Bild wird aus der Datenbank entfernt.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
                  Löschen
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Image Preview */}
        <Card>
          <CardContent className="p-4">
            <div className="aspect-video rounded-lg overflow-hidden bg-slate-100 flex items-center justify-center">
              <div className="w-full h-full bg-gradient-to-br from-slate-200 to-slate-300 flex items-center justify-center">
                <Images className="w-16 h-16 text-slate-400" />
              </div>
            </div>
            
            {badge && (
              <div className="mt-4">
                <Badge className={badge.color}>{badge.label}</Badge>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Metadata */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Informationen</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                <User className="w-5 h-5 text-slate-400" />
                <div>
                  <p className="text-xs text-slate-500">Name</p>
                  <p className="font-medium text-slate-900">{image.sender_name}</p>
                </div>
              </div>
              
              <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                <Mail className="w-5 h-5 text-slate-400" />
                <div>
                  <p className="text-xs text-slate-500">E-Mail</p>
                  <p className="font-medium text-slate-900 truncate">{image.sender_email}</p>
                </div>
              </div>
              
              <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                <Calendar className="w-5 h-5 text-slate-400" />
                <div>
                  <p className="text-xs text-slate-500">Datum</p>
                  <p className="font-medium text-slate-900">{image.upload_date}</p>
                </div>
              </div>
              
              <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                <Clock className="w-5 h-5 text-slate-400" />
                <div>
                  <p className="text-xs text-slate-500">Uhrzeit</p>
                  <p className="font-medium text-slate-900">{image.upload_time}</p>
                </div>
              </div>
              
              <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                <Eye className="w-5 h-5 text-slate-400" />
                <div>
                  <p className="text-xs text-slate-500">Downloads</p>
                  <p className="font-medium text-slate-900">{image.download_count}</p>
                </div>
              </div>
              
              <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                <Images className="w-5 h-5 text-slate-400" />
                <div>
                  <p className="text-xs text-slate-500">Gast-Uploads</p>
                  <p className="font-medium text-slate-900">{image.guest_upload_count}</p>
                </div>
              </div>
            </div>

            {image.description && (
              <div className="p-3 bg-slate-50 rounded-lg">
                <div className="flex items-start gap-3">
                  <FileText className="w-5 h-5 text-slate-400 mt-0.5" />
                  <div>
                    <p className="text-xs text-slate-500 mb-1">Beschreibung</p>
                    <p className="text-slate-700">{image.description}</p>
                  </div>
                </div>
              </div>
            )}

            {image.first_download_date && (
              <div className="text-sm text-slate-500">
                <p>Erster Download: {image.first_download_date}</p>
                {image.last_download_date && (
                  <p>Letzter Download: {image.last_download_date}</p>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Edit Section */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-lg">Bearbeiten</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Categories */}
            <div>
              <Label className="mb-3 block">Kategorien</Label>
              <div className="flex flex-wrap gap-2">
                {categories.length > 0 ? categories.map(cat => (
                  <button
                    key={cat.id}
                    onClick={() => toggleCategory(cat.id)}
                    className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                      selectedCategories.includes(cat.id)
                        ? 'bg-primary text-white'
                        : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                    }`}
                    data-testid={`category-${cat.id}`}
                  >
                    {selectedCategories.includes(cat.id) && <Check className="w-3 h-3 inline mr-1" />}
                    {cat.name}
                  </button>
                )) : (
                  <p className="text-sm text-slate-500">Keine Kategorien vorhanden. <Link to="/admin/categories" className="text-primary hover:underline">Kategorien erstellen</Link></p>
                )}
              </div>
            </div>

            {/* Usage Status */}
            <div>
              <Label className="mb-2 block">Verwendungsstatus</Label>
              <Select value={usageStatus} onValueChange={setUsageStatus}>
                <SelectTrigger className="w-48" data-testid="usage-status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="nicht_verwendet">Nicht verwendet</SelectItem>
                  <SelectItem value="verwendet">Verwendet</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Note */}
            <div>
              <Label htmlFor="note" className="mb-2 block">Notiz</Label>
              <Textarea
                id="note"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Interne Notizen zum Bild..."
                rows={3}
                data-testid="note-textarea"
              />
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={fetchData} data-testid="cancel-button">
                Abbrechen
              </Button>
              <Button onClick={handleSave} disabled={saving} data-testid="save-button">
                {saving ? 'Speichern...' : 'Speichern'}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ImageDetailPage;
